import { LIMITS } from './constants';
import type { AnnouncementAudience, AnnouncementDoc, RegistrantType } from './models';

/** כללי עדכונים וצ'אט — משותפים לממשק ולשרת. */

export function threadIdFor(jobId: string, contractorId: string): string {
  return `${jobId}__${contractorId}`;
}

export function parseThreadId(threadId: string): { jobId: string; contractorId: string } | null {
  const m = /^([A-Za-z0-9]{20})__([0-9a-f]{32}|d\d{8,15})$/.exec(threadId);
  return m ? { jobId: m[1]!, contractorId: m[2]! } : null;
}

export type TextValidation = { ok: true; value: string } | { ok: false; error: string };

export function validateMessageText(text: unknown, max: number = LIMITS.maxMessageLength): TextValidation {
  const v = typeof text === 'string' ? text.replace(/\r\n/g, '\n').trim() : '';
  if (!v) return { ok: false, error: 'אי אפשר לשלוח הודעה ריקה' };
  if (v.length > max) return { ok: false, error: `ההודעה ארוכה מדי (עד ${max} תווים)` };
  return { ok: true, value: v };
}

const AUDIENCES: AnnouncementAudience[] = ['all', 'contractors', 'workers', 'selected'];

export type AnnouncementValidation =
  | { ok: true; value: { text: string; audience: AnnouncementAudience; recipientIds: string[]; showOnRegistration: boolean } }
  | { ok: false; error: string };

export function validateAnnouncement(input: {
  text?: unknown;
  audience?: unknown;
  recipientIds?: unknown;
  showOnRegistration?: unknown;
}): AnnouncementValidation {
  const t = validateMessageText(input.text, LIMITS.maxAnnouncementLength);
  if (!t.ok) return { ok: false, error: t.error === 'אי אפשר לשלוח הודעה ריקה' ? 'יש לכתוב את תוכן העדכון' : t.error };
  const audience = input.audience as AnnouncementAudience;
  if (!AUDIENCES.includes(audience)) return { ok: false, error: 'יש לבחור למי העדכון מיועד' };
  let recipientIds: string[] = [];
  if (audience === 'selected') {
    if (!Array.isArray(input.recipientIds) || input.recipientIds.length === 0) return { ok: false, error: 'יש לבחור לפחות נרשם אחד' };
    if (input.recipientIds.length > 500 || !input.recipientIds.every((x) => typeof x === 'string' && x.length <= 64)) {
      return { ok: false, error: 'רשימת נמענים לא תקינה' };
    }
    recipientIds = [...new Set(input.recipientIds as string[])];
  }
  return { ok: true, value: { text: t.value, audience, recipientIds, showOnRegistration: audience === 'all' && input.showOnRegistration === true } };
}

/** האם העדכון מיועד לנרשם הזה. */
export function announcementVisibleTo(
  a: Pick<AnnouncementDoc<unknown>, 'audience' | 'recipientIds'>,
  reg: { contractorId: string; registrantType: RegistrantType },
): boolean {
  switch (a.audience) {
    case 'all':
      return true;
    case 'contractors':
      return reg.registrantType === 'contractor';
    case 'workers':
      return reg.registrantType === 'worker';
    case 'selected':
      return a.recipientIds.includes(reg.contractorId);
  }
}

export const AUDIENCE_LABELS: Record<AnnouncementAudience, string> = {
  all: 'כל הנרשמים',
  contractors: 'קבלנים',
  workers: 'עובדים',
  selected: 'נרשמים מסוימים',
};
